import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Container, Text, Sprite, Graphics } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import * as PIXI from "pixi.js";
import SpineTextureExtractor from "../../../../utils/SpineTextureExtractor";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import anime from "animejs";
import { useDimensionsWhen } from "../../../../Contexts/DimensionsContext";
import { useReelControl } from "../../../../Contexts/ReelControlContext";
import { getNewUITexture } from "../../../../assets";
import ButtonShadow from "../Buttons/ButtonShadow";
import {
  FONT_CONFIG,
  PixiSettings,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../pixiSettings";
import { outlineShadow } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
import { closeBonusBuy } from "../../../../State/actions";
import {
  selectBonusBuyActive,
  selectCurrency,
  selectBalance,
  selectIsSpinning,
  selectCounterAnimationFinished,
  selectCriticalAnimationPlaying,
  selectBonusBuyMultipliers,
  selectBonusBuyFeatureName,
  selectBonusBuyFeatures,
} from "../../../../State/selectors";
import { formatToCustomLocale } from "../../../../utilities";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";
import { triggerScalePress } from "../../../../Hooks/useButtonHover";
import BonusBetSelector from "../Displays/BonusBetSelector";

// ── Overlay ──────────────────────────────────────────────────────────────────
const OVERLAY_ALPHA = 0.7;
const OVERLAY_COLOR = 0x000000;
const OVERLAY_FADE_MS = 200;

// ── Layout config ───────────────────────────────────────────────────────────
// Card dimensions come from popup_frame_bonus texture (native width/height).
// All ratios below are relative to cardWidth so everything scales dynamically.
const POPUP_CONFIG = {
  // Ratios relative to cardWidth (cardWidth derived from texture)
  cardGapRatio: 0.05, // Gap between cards (landscape/desktop)
  cardGapPortraitRatio: 0.075, // Gap between cards (portrait)
  paddingTopRatio: 0.05, // Inner content padding (top)
  paddingBottomRatio: 0.07, // Inner content padding (bottom)
  titleFontRatio: 0.07, // Title font size
  // Description size. Lowered from 0.055 when the single-line fit was
  // replaced by the two-line one below: that fit no longer collapses the text,
  // so the ideal became the size actually drawn — and 0.055 was a ceiling
  // nothing ever reached. 0.044 is the ceiling Vladimir asked for, 2026-09-18:
  // clearly bigger than the ~14px the old fit produced, not a jump to 24.
  subtitleFontRatio: 0.044, // Subtitle font size
  // Phone landscape (2026-10-08, with the bigger cards: "way too small"): the
  // title and description start ~20% bigger there.
  titleFontRatioMobileLandscape: 0.085,
  // subtitleFontRatioMobileLandscape: 0.052,
  // 0.052 was ~10px on a ~205px card — still "really small". ~13px now, on up
  // to three lines (DESC_MAX_LINES_MOBILE_LANDSCAPE).
  subtitleFontRatioMobileLandscape: 0.064,
  titleSubGapRatio: 0.075, // Gap between title and subtitle
  imageBottomGapRatio: 0.053, // Gap below image
  // ── The volatility + xBet block, between the image and the BUY button ──
  //
  // From the new card design. NOTHING in the API describes volatility — it is
  // not in feature_options, the round, or anywhere else — so the rating is
  // authored (see VOLATILITY below) and is a marketing value, not a computed one.
  infoRowGapRatio: 0.035, // Between the two rows
  infoBlockGapRatio: 0.05, // Between the block and the BUY button
  volatilityDotRatio: 0.042, // Dot diameter — see VolatilityRow
  volatilityDotGapRatio: 0.022, // Gap between dots
  volatilityLabelGapRatio: 0.03, // Between the word and the first dot
  // The "Volatility ●●●●●" and "N x bet" rows. Their OWN ratio, not the
  // description's size — see infoFontSize.
  infoFontRatio: 0.044,
  buyButtonWidthRatio: 0.6, // Buy button width
  buyFontRatio: 0.069, // Buy button text
  priceFontRatio: 0.065, // Price text
  // Phone landscape: the card is ~200px wide, so the price came out ~12px and
  // read small (3 Orbs of Zeus e87f8036, 2026-10-08). A little bigger there,
  // with more of the button's height for the two lines so the fit does not
  // shrink it straight back.
  priceFontRatioMobileLandscape: 0.07,
  buttonTextHeightRatio: 0.85,
  buttonTextHeightRatioMobileLandscape: 0.95,
  betSelectorGapRatio: 0.14, // Gap between bet selector and cards (landscape/desktop)
  betSelectorGapPortraitRatio: 0.29, // Gap between bet selector and cards (portrait)
  rowMaxWidthRatio: 0.92, // Max share of screen width a row of cards may use
  betSelectorScaleRatio: 0.575, // Bet selector size relative to popupScale
  closeButtonOffsetRatio: 0.05, // Close button offset (% of viewport height)

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.76,
        // yOffset: 0.45,
        yOffset: 0.5, // a tiny bit down: the X and BET touched the top (2026-10-09)
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        // deviceMultiplier: 1.075,
        // 1.075 -> 1.45 (3 Orbs of Zeus e87f8036, 2026-10-08): the row used ~59%
        // of the width and ~40% of the height. rowMaxWidthRatio still caps the
        // row on a narrow phone.
        deviceMultiplier: 1.45,
        yOffset: 0.0,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.75,
        yOffset: 0.475,
        betSelectorScaleRatio: 0.55,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        // deviceMultiplier: 1.075,
        // 1.075 -> 1.45 (3 Orbs of Zeus e87f8036, 2026-10-08): the row used ~59%
        // of the width and ~40% of the height. rowMaxWidthRatio still caps the
        // row on a narrow phone.
        deviceMultiplier: 1.45,
        yOffset: 0.0,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.5,
        yOffset: 0,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.3,
        yOffset: 0,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: { deviceMultiplier: 1.15, yOffset: 0.02 },
  },
};

// Measures `text` at `idealFontSize` (single line) and shrinks it (in 2px
// steps) so it fits within `maxWidth`, or returns the ideal size unshrunk.
// Shared by the title and description fit calculations below, and reused by
// the parent (to compute a size shared across both cards) and by BuyCard
// itself (fallback when no shared size is passed in).
const fitFontSizeToWidth = (text, idealFontSize, maxWidth, fontWeight = "bold") => {
  const tempStyle = new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: idealFontSize,
    fontWeight,
    wordWrap: false,
  });
  const metrics = TextMetrics.measureText(text, tempStyle);
  if (metrics.width > maxWidth) {
    const scale = maxWidth / metrics.width;
    return Math.round((idealFontSize * scale) / 2) * 2;
  }
  return idealFontSize;
};

/**
 * How many lines a card description may use before the font has to shrink.
 *
 * The old fit allowed ONE, implicitly: it measured with wordWrap off, so the
 * size it returned was whatever made the whole sentence fit on a single line.
 * That was invisible with Zeus's copy ("1 or more random features") and brutal
 * with the game rules' ("Get one of the other 3 Free Spins options at random")
 * — the longest description decided a size for all four cards, and the text
 * came out tiny. Vladimir, 2026-09-18.
 */
const DESC_MAX_LINES = 2;
// Phone landscape: the description may take a THIRD line rather than shrink
// back to fit two (2026-10-08: "descriptions are still really small").
const DESC_MAX_LINES_MOBILE_LANDSCAPE = 3;
// The BET selector's reach ABOVE its centre, in its own scale's units:
// half its box (110 / 2) + the label gap (25) + the label (64px font, ~80
// tall) — BonusBetSelector's REFERENCE_CONFIG (2026-10-30).
const BET_SELECTOR_ABOVE_REF = 160;
// Clear room kept at the top and the bottom of the screen, x its height.
const POPUP_FIT_MARGIN_RATIO = 0.02;

/**
 * How far the text above a card's image may shrink so the image keeps its full
 * size — see cardFit in BuyBonusPopup. Steps of 5%, never below 70%.
 */
const CARD_FIT_STEP = 0.05;
// const CARD_FIT_MIN_SCALE = 0.7;
// The text never shrinks below 85% now (2026-10-08) — at 0.7 it read "way too
// small" on the bigger phone-landscape cards; the image gives way instead.
const CARD_FIT_MIN_SCALE = 0.85;

/**
 * The largest size at which `text` WRAPS into at most `maxLines` rows.
 *
 * The honest measurement for a paragraph that is drawn with wordWrap on: it
 * asks the same question the renderer will. fitFontSizeToWidth above still
 * suits the TITLE, which is short and must stay on one line.
 *
 * Steps down in 2px, the same grain as the ideal sizes, and never scales up —
 * the ideal is the ceiling.
 */
const fitFontSizeToLines = (
  text,
  idealFontSize,
  maxWidth,
  maxLines = DESC_MAX_LINES,
  fontWeight = "500",
) => {
  // ── Never fewer lines than the translator forced (2026-10-30, Thai) ── a
  // description with hard breaks ("\n" — the Thai H&W one has three lines)
  // could never come down to `maxLines`, so the loop shrank it to its 8px
  // floor, and both cards share the smaller size. Those lines are allowed;
  // the size is still the largest at which each one fits the card.
  const forcedLines = String(text ?? "").split("\n").length;
  const allowedLines = Math.max(maxLines, forcedLines);
  for (let size = idealFontSize; size > 8; size -= 2) {
    const metrics = TextMetrics.measureText(
      text,
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: size,
        fontWeight,
        wordWrap: true,
        wordWrapWidth: maxWidth,
      }),
    );
    // if (metrics.lines.length <= maxLines) return size;
    if (metrics.lines.length <= allowedLines) return size;
  }
  return 8;
};

// Bonus tier definitions — prices come from Redux (populated by API init).
//
// Mad Labs has FOUR buy levels (GDD §10). `featureLevel` is the only field
// the API sees: it becomes `purchased_feature_level` on the spin request, so
// these four values are what actually decide which package is bought.
//
// ARTWORK IS NOW MAD LABS' OWN, one image per level, and it MATCHES the tier
// it buys — the purple card shows 7/8/9 with no pips, red the same spins with
// two, gold 8/9/10 with four, and the fourth shows one of each behind a "?".
// No per-card scale or offset: the four are a consistent set at about
// 306x210, so the layout's own fit rule sizes them and they cannot drift
// apart. The Zeus images had to be nudged individually because they were two
// unrelated pictures reused four times.
//
// TITLES AND DESCRIPTIONS are the game rules' own copy, 2026-09-18, keyed in
// public/translations/translations.json (game namespace). English only so far:
// a locale that has not been translated yet falls back to that file, so the
// card reads English rather than printing a raw key.
// ── Volatility: FIVE DOTS ON EVERY CARD ──
//
// Out of 5, authored here and never sent by the API — nothing in
// feature_options, the round or anywhere else describes volatility.
//
// It was a per-tier rating (3 / 4 / 5 / 5) until Vladimir checked it against
// the card design, 2026-09-18: all four cards are drawn with the full row lit.
// One constant rather than four equal numbers, so the row cannot drift apart
// again by editing one card.
const VOLATILITY = 5;

// const BONUS_TIER_CONFIGS = [
//   {
//     key: "fs_low",
//     volatility: VOLATILITY,
//     titleKey: "bonus_buy_free_spins_low_title",
//     descKey: "bonus_buy_free_spins_low_description",
//     featureLevel: "1", // Purple — 7/8/9 spins from Level 0
//     featureImage: "bonus_buy_1",
//   },
//   {
//     key: "fs_mid",
//     volatility: VOLATILITY,
//     titleKey: "bonus_buy_free_spins_mid_title",
//     descKey: "bonus_buy_free_spins_mid_description",
//     featureLevel: "2", // Red — 7/8/9 spins from Level 2
//     featureImage: "bonus_buy_2",
//   },
//   {
//     key: "fs_high",
//     volatility: VOLATILITY,
//     titleKey: "bonus_buy_free_spins_high_title",
//     descKey: "bonus_buy_free_spins_high_description",
//     featureLevel: "3", // Gold — 8/9/10 spins from Level 4, the top package
//     featureImage: "bonus_buy_3",
//   },
//   {
//     key: "fs_random",
//     volatility: VOLATILITY,
//     titleKey: "bonus_buy_mystery_free_spins_title",
//     descKey: "bonus_buy_mystery_free_spins_description",
//     featureLevel: "4", // Mystery — one of the three above, at random
//     featureImage: "bonus_buy_4",
//   },
// ];
// ── Cleopatra's Fire: the server's FOUR buys (init feature_multipliers,
// verified on the real dev server 2026-09-30) ── `featureName` becomes
// `purchased_feature`, `featureLevel` `purchased_feature_level`:
//   free spins    freespin_buy 1 (30×) · freespin_buy 2 (50×)
//   Hold & Win    bonus_buy 1 (35×)    · bonus_buy 2 (70×)
// Titles and art are still Mad Labs' — placeholders (Vladimir: "we dont care
// about that now").
// Art since 2026-10-02: free spins = startpage_image_4, Hold & Win =
// startpage_image_1 (the intro's pictures).
const BONUS_TIER_CONFIGS = [
  // ── Removed by the server (2026-10-22: "we have removed one free spin
  // bonus and one HW bonus … keeping bb_fs_50 and bb_hw_70") — level 2 stays. ──
  // {
  //   key: "fs_1",
  //   volatility: VOLATILITY,
  //   // titleKey: "bonus_buy_free_spins_low_title",
  //   // descKey: "bonus_buy_free_spins_low_description",
  //   // Both free spins cards the same for now (Vladimir, 2026-09-30).
  //   // titleKey: "bonus_buy_free_spins_title",
  //   titleKey: "freespins", // shared translation (2026-10-13)
  //   descKey: "bonus_buy_free_spins_description",
  //   featureName: "freespin_buy",
  //   featureLevel: "1",
  //   // featureImage: "bonus_buy_1", — Mad Labs art, removed (2026-09-30)
  //   // // The scatter's own picture stands for Free Spins until the art arrives.
  //   // symbolImage: "sc",
  //   // The card art (Vladimir, 2026-10-02): the intro's free-spins board.
  //   featureImage: "startpage_image_4",
  //   // Its art is 86% of the file — a touch smaller so it clears the
  //   // volatility line and matches the Hold & Win card's (2026-10-05).
  //   // imageScale: 0.9,
  //   // Bigger into the rows' space (2026-10-14: "allow them to grow a bit"):
  //   // the art is visible over 89% of its height, so ~93% of the band.
  //   imageScale: 1.05,
  //   // The text fit (cardFit) keeps the old size — the text stays as it was.
  //   textFitImageScale: 0.9,
  //   // Where the art's SOLID frame starts, a share of the texture's height
  //   // (alpha >= 250 at row 61 of 651) — for lining the cards' tops up.
  //   visibleTop: 61 / 651,
  // },
  {
    key: "fs_2",
    volatility: VOLATILITY,
    // titleKey: "bonus_buy_free_spins_mid_title",
    // descKey: "bonus_buy_free_spins_mid_description",
    // Both free spins cards the same for now (Vladimir, 2026-09-30).
    // titleKey: "bonus_buy_free_spins_title",
    titleKey: "freespins", // shared translation (2026-10-13)
    descKey: "bonus_buy_free_spins_description",
    featureName: "freespin_buy",
    featureLevel: "2",
    // featureImage: "bonus_buy_2", — Mad Labs art, removed (2026-09-30)
    // // The scatter's own picture stands for Free Spins until the art arrives.
    // symbolImage: "sc",
    // The card art (Vladimir, 2026-10-02): the intro's free-spins board.
    featureImage: "startpage_image_4",
    // Its art is 86% of the file — a touch smaller so it clears the
    // volatility line and matches the Hold & Win card's (2026-10-05).
    // imageScale: 0.9,
    // Bigger into the rows' space (2026-10-14: "allow them to grow a bit"):
    // the art is visible over 89% of its height, so ~93% of the band.
    imageScale: 1.05,
    // The text fit (cardFit) keeps the old size — the text stays as it was.
    textFitImageScale: 0.9,
    // Where the art's SOLID frame starts, a share of the texture's height
    // (alpha >= 250 at row 61 of 651) — for lining the cards' tops up.
    visibleTop: 61 / 651,
  },
  // ── Removed by the server (2026-10-22: "we have removed one free spin
  // bonus and one HW bonus … keeping bb_fs_50 and bb_hw_70") — level 2 stays. ──
  // {
  //   key: "hnw_1",
  //   volatility: VOLATILITY,
  //   // titleKey: "bonus_buy_free_spins_high_title",
  //   // descKey: "bonus_buy_free_spins_high_description",
  //   // Both Hold & Win cards the same for now (Vladimir, 2026-09-30).
  //   titleKey: "bonus_buy_hold_and_win_title",
  //   descKey: "bonus_buy_hold_and_win_description",
  //   featureName: "bonus_buy",
  //   featureLevel: "1",
  //   // featureImage: "bonus_buy_3", — Mad Labs art, removed (2026-09-30)
  //   // // The coin's own picture stands for Hold & Win until the card art arrives.
  //   // symbolImage: "c",
  //   // The card art (Vladimir, 2026-10-02): the intro's Hold & Win board.
  //   featureImage: "startpage_image_1",
  //   // Its art is only 70% of the file (glow around it) — bigger, to match
  //   // the free-spins card's art (0.70 x 1.1 ~ 0.86 x 0.9; 2026-10-05).
  //   // imageScale: 1.1,
  //   // imageScale: 1.2, // a bit more (2026-10-05)
  //   // Bigger into the rows' space (2026-10-14): the art is visible over only
  //   // 73% of its height (glow margins), so ~95% of the band.
  //   // imageScale: 1.3,
  //   // imageScale: 1.36, // a tiny bit more (2026-10-14)
  //   // Bigger again, growing DOWN under the BUY button (2026-10-14: "let them
  //   // go under the button"): the top stays where the 1.3 image's top was.
  //   // imageScale: 1.45,
  //   imageScale: 1.5,
  //   // imageTopAtScale: 1.3,
  //   // The art's TOP on the free-spins art's top (2026-10-14: "i want those
  //   // edges to align at the top"); it grows down from there, under the BUY
  //   // button. Its solid frame starts at row 223 of 1134 (the glow above it
  //   // does not count — first try aligned the glow and nothing moved).
  //   alignTopWith: "fs_1",
  //   visibleTop: 223 / 1134,
  //   imageYOffset: 0.008, // a tiny bit down from the line (2026-10-14: "overdid it")
  //   textFitImageScale: 1.2,
  // },
  {
    key: "hnw_2",
    volatility: VOLATILITY,
    // titleKey: "bonus_buy_mystery_free_spins_title",
    // descKey: "bonus_buy_mystery_free_spins_description",
    // Both Hold & Win cards the same for now (Vladimir, 2026-09-30).
    titleKey: "bonus_buy_hold_and_win_title",
    descKey: "bonus_buy_hold_and_win_description",
    featureName: "bonus_buy",
    featureLevel: "2",
    // featureImage: "bonus_buy_4", — Mad Labs art, removed (2026-09-30)
    // // The coin's own picture stands for Hold & Win until the card art arrives.
    // symbolImage: "c",
    // The card art (Vladimir, 2026-10-02): the intro's Hold & Win board.
    featureImage: "startpage_image_1",
    // Its art is only 70% of the file (glow around it) — bigger, to match
    // the free-spins card's art (0.70 x 1.1 ~ 0.86 x 0.9; 2026-10-05).
    // imageScale: 1.1,
    // imageScale: 1.2, // a bit more (2026-10-05)
    // Bigger into the rows' space (2026-10-14): the art is visible over only
    // 73% of its height (glow margins), so ~95% of the band.
    // imageScale: 1.3,
    // imageScale: 1.36, // a tiny bit more (2026-10-14)
    // Bigger again, growing DOWN under the BUY button (2026-10-14: "let them
    // go under the button"): the top stays where the 1.3 image's top was.
    // imageScale: 1.45,
    imageScale: 1.5,
    // imageTopAtScale: 1.3,
    // The art's TOP on the free-spins art's top (2026-10-14: "i want those
    // edges to align at the top"); it grows down from there, under the BUY
    // button. Its solid frame starts at row 223 of 1134 (the glow above it
    // does not count — first try aligned the glow and nothing moved).
    // alignTopWith: "fs_1",
    alignTopWith: "fs_2", // fs_1 is gone; fs_2 has the same art, scale and top line
    visibleTop: 223 / 1134,
    imageYOffset: 0.008, // a tiny bit down from the line (2026-10-14: "overdid it")
    textFitImageScale: 1.2,
  },
];

// ═════════════════════════════════════════════════════════════════════════════
/** An image fitted into availW x availH (aspect kept), times `scale`. */
const fitImageSize = (texture, availW, availH, scale = 1) => {
  if (!texture || availH <= 0) return null;
  const ar = texture.width / texture.height;
  const h = availH * ar <= availW ? availH : availW / ar;
  return { width: h * ar * scale, height: h * scale };
};

// ── No volatility / "x bet" rows on Cleopatra's cards (2026-10-14: "i dont
// need that for this game at all … expand image to fill empty space") ── the
// two rows are not drawn and the image's band runs down to the BUY button.
// The text is untouched: the popup's text fit still reserves the rows' height
// (cardFit), so no font size changes. true brings the rows back.
const SHOW_INFO_ROWS = false;

// BuyCard — a single bonus option card with title, subtitle, image, buy button
// ═════════════════════════════════════════════════════════════════════════════
/**
 * The "Volatility ●●●●●" line — label and dots centred as one group.
 *
 * Split out so the measuring stays readable: the label's width is only known
 * once PIXI has measured the text, so the group's left edge is derived rather
 * than assumed. Positioning the two independently looked right in English and
 * drifted in every other language.
 *
 * @param {number} filled How many of TOTAL_DOTS are lit.
 */
const VolatilityRow = ({
  cx,
  y,
  filled,
  label,
  style,
  dotSize,
  dotGap,
  labelGap,
}) => {
  const TOTAL_DOTS = 5;

  // ── A TEXTURE, because this renderer has no antialiasing ──
  //
  // Stage.js runs the app with `antialias: false`, so ANY vector circle has
  // hard stair-stepped edges here however finely it is tessellated. That is
  // what the jagged dots were — not the segment count, which is what I chased
  // first. A sprite carries its own soft alpha edge and stays clean at any size.
  //
  // `button_buy_bonus_background` is the round yellow BUY BONUS button, 181x181
  // in user_interface.json — already the right shape and the right yellow, and
  // scaling it down to a dot keeps it crisp.
  const dotTexture = useMemo(
    () => getNewUITexture("button_buy_bonus_background"),
    [],
  );

  const labelW = useMemo(
    () => Math.ceil(TextMetrics.measureText(label, style).width),
    [label, style],
  );

  const dotsW = TOTAL_DOTS * dotSize + (TOTAL_DOTS - 1) * dotGap;
  const groupW = labelW + labelGap + dotsW;
  const left = cx - groupW / 2;
  const dotsLeft = left + labelW + labelGap;

  return (
    <Container y={y}>
      <Text text={label} style={style} x={left} anchor={[0, 0.5]} />
      {dotTexture &&
        Array.from({ length: TOTAL_DOTS }, (_, i) => (
          <Sprite
            key={i}
            texture={dotTexture}
            x={dotsLeft + i * (dotSize + dotGap) + dotSize / 2}
            y={0}
            width={dotSize}
            height={dotSize}
            anchor={0.5}
            // Lit dots keep the button's own yellow. Unlit are the same object
            // faded, so the row reads as one set rather than two shapes.
            alpha={i < filled ? 1 : 0.25}
          />
        ))}
    </Container>
  );
};

const BuyCard = ({
  tier,
  cardWidth,
  cardHeight,
  currentBet,
  currency,
  canAfford,
  onBuy,
  sharedTitleFontSize,
  sharedSubtitleFontSize,
  sharedTitleAreaHeight,
  sharedSubtitleAreaHeight,
  sharedLayoutScale,
  fitImageToBand = false,
}) => {
  const { t } = useTranslation();
  const { buttonRef, handlePointerOver, handlePointerOut, cleanupFilters } =
    useButtonBrightness();

  const isProcessingRef = useRef(false);
  const buyButtonContainerRef = useRef(null);

  // Clean up filters on unmount
  useEffect(() => () => cleanupFilters(), [cleanupFilters]);

  // ── All dimensions derived from cardWidth ───────────────────────────────
  const paddingTop = cardWidth * POPUP_CONFIG.paddingTopRatio;
  const paddingBottom = cardWidth * POPUP_CONFIG.paddingBottomRatio;
  const padding = cardWidth * POPUP_CONFIG.paddingBottomRatio; // sides use bottom ratio
  const contentWidth = cardWidth - padding * 2;

  // ── Text styles ─────────────────────────────────────────────────────────
  const dpr = window.devicePixelRatio || 1;
  const idealTitleFontSize =
    Math.round((cardWidth * POPUP_CONFIG.titleFontRatio) / 2) * 2;
  const idealSubtitleFontSize =
    Math.round((cardWidth * POPUP_CONFIG.subtitleFontRatio) / 2) * 2;
  // const titleSubGap = cardWidth * POPUP_CONFIG.titleSubGapRatio;
  // The gaps around the text shrink with it — see cardFit in the popup.
  const layoutScale = sharedLayoutScale ?? 1;
  const titleSubGap = cardWidth * POPUP_CONFIG.titleSubGapRatio * layoutScale;

  // Auto-scale title/description font to fit within card content width. When
  // a shared size is provided (computed across all cards by the parent), use
  // it so every card's text renders at the same size — otherwise a long title
  // or description on one card shrinks while a short one on the other stays
  // at full size, making the two look mismatched.
  const ownTitleFontSize = useMemo(
    () =>
      fitFontSizeToWidth(
        t(tier.titleKey).toUpperCase(),
        idealTitleFontSize,
        contentWidth,
        "bold",
      ),
    [idealTitleFontSize, contentWidth, t, tier.titleKey],
  );
  const titleFontSize = sharedTitleFontSize ?? ownTitleFontSize;

  const ownSubtitleFontSize = useMemo(
    () =>
      fitFontSizeToLines(
        t(tier.descKey),
        idealSubtitleFontSize,
        contentWidth,
      ),
    [idealSubtitleFontSize, contentWidth, t, tier.descKey],
  );
  const subtitleFontSize = sharedSubtitleFontSize ?? ownSubtitleFontSize;
  // const imageBottomGap = cardWidth * POPUP_CONFIG.imageBottomGapRatio;
  const imageBottomGap =
    cardWidth * POPUP_CONFIG.imageBottomGapRatio * layoutScale;

  // ── The volatility + "N x bet" block ──
  //
  // ── Its OWN size, and no longer the description's ──
  //
  // It used to take subtitleFontSize so the two would read as one family. But
  // that size is FITTED to the longest description on any card, and these two
  // rows are short — "Volatility" and "100 x bet" never need shrinking. So a
  // long sentence on one card was dragging the whole block down with it, which
  // is what made these unreadable.
  //
  // A ratio of the card instead: every card shows them at the same size,
  // because the card width is the same, and no amount of copy can shrink them.
  const infoFontSize =
    Math.round((cardWidth * POPUP_CONFIG.infoFontRatio) / 2) * 2;
  const infoRowGap = cardWidth * POPUP_CONFIG.infoRowGapRatio;
  const infoBlockGap = cardWidth * POPUP_CONFIG.infoBlockGapRatio;
  const dotSize = cardWidth * POPUP_CONFIG.volatilityDotRatio;
  const dotGap = cardWidth * POPUP_CONFIG.volatilityDotGapRatio;
  const dotLabelGap = cardWidth * POPUP_CONFIG.volatilityLabelGapRatio;
  // Two text rows plus the gap between them. The dots are smaller than the
  // text, so the row height is the text's.
  const infoBlockH = infoFontSize * 2 + infoRowGap;
  // The rows' share of the card — nothing when they are off (SHOW_INFO_ROWS).
  const infoReserveH = SHOW_INFO_ROWS ? infoBlockGap + infoBlockH : 0;

  const titleStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: titleFontSize,
        fontWeight: "bold",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentWidth,
        resolution: dpr,
        // ...TEXT_STYLE_CONFIG.OUTLINE,
        ...outlineShadow(titleFontSize), // shadow scales with the text
      }),
    [titleFontSize, contentWidth, dpr],
  );

  // Same family and outline as the subtitle — this block is supporting copy,
  // not a heading. Kept as its own style so its size can move independently.
  const infoStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: infoFontSize,
        // ── IDENTICAL to the description, weight included ──
        //
        // It first shipped bold, grey and OUTLINED — the outline read as a
        // shadow the description does not have. Then 600, one step heavier.
        // Settled on matching it exactly: same family, size, colour and weight,
        // so this block is plainly the description's continuation.
        fontWeight: "500",
        fill: 0xffffff,
        align: "center",
        resolution: dpr,
        // NO outline. The description carries none, and this must match it —
        // do not add TEXT_STYLE_CONFIG.OUTLINE here.
      }),
    [infoFontSize, dpr],
  );

  const subtitleStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: subtitleFontSize,
        fontWeight: "500",
        fill: 0xffffff,
        align: "center",
        wordWrap: true,
        wordWrapWidth: contentWidth,
        lineHeight: Math.round(subtitleFontSize * 1.2),
        resolution: dpr,
      }),
    [subtitleFontSize, contentWidth, dpr],
  );

  // ── Textures ────────────────────────────────────────────────────────────
  // const featureTexture = useMemo(
  //   () => PIXI.Assets.get(tier.featureImage),
  //   [tier.featureImage],
  // );
  // A card may show a SYMBOL's picture (its spine, trimmed) in place of an
  // art file — the Hold & Win cards' coin, until their art arrives.
  const featureTexture = useMemo(
    () =>
      tier.symbolImage
        ? SpineTextureExtractor.getTrimmedTexture(tier.symbolImage)
        : PIXI.Assets.get(tier.featureImage),
    [tier.featureImage, tier.symbolImage],
  );
  const buttonTexture = useMemo(() => getNewUITexture("button_bonus_card"), []);
  const buttonDisabledTexture = useMemo(
    () => getNewUITexture("button_bonus_card_disabled"),
    [],
  );
  // The card background sprite — its shadow follows it (ButtonShadow).
  const cardBgRef = useRef(null);
  const cardBgTexture = useMemo(
    () => getNewUITexture("bonus_card_background"),
    [],
  );

  // ── Button config (all relative to cardWidth) ──────────────────────────
  const btnW = cardWidth * POPUP_CONFIG.buyButtonWidthRatio;
  const btnAR = buttonTexture
    ? buttonTexture.width / buttonTexture.height
    : 270 / 90;
  const btnH = btnW / btnAR;
  const buyFontSize =
    Math.round((cardWidth * POPUP_CONFIG.buyFontRatio) / 2) * 2;
  const priceFontSize =
    // Math.round((cardWidth * POPUP_CONFIG.priceFontRatio) / 2) * 2;
    Math.round(
      (cardWidth *
        (fitImageToBand
          ? POPUP_CONFIG.priceFontRatioMobileLandscape
          : POPUP_CONFIG.priceFontRatio)) /
        2,
    ) * 2;

  // A tier with no advertised price still has to be buyable — the server is
  // the authority on cost and will reject the purchase if the balance is
  // short. Blocking the button instead would make an unpriced tier unusable.
  const hasPrice = typeof tier.multiplier === "number" && tier.multiplier > 0;
  const cost = hasPrice ? currentBet * tier.multiplier : 0;
  const priceText = hasPrice
    ? `${formatToCustomLocale(cost)} ${currency}`
    : `— ${currency}`;

  // ── Auto-scale BUY + price text to fit button bounds ────────────────────
  const maxTextWidth = btnW * 0.9; // 90% of button width
  // const maxTextHeight = btnH * 0.85; // 85% of button height for both texts combined
  // Share of the button's height for both texts together — see
  // buttonTextHeightRatio. `fitImageToBand` is true on phone landscape only.
  const maxTextHeight =
    btnH *
    (fitImageToBand
      ? POPUP_CONFIG.buttonTextHeightRatioMobileLandscape
      : POPUP_CONFIG.buttonTextHeightRatio);
  const textGap = btnH * 0.05; // small gap between the two lines

  const { scaledBuyFontSize, scaledPriceFontSize } = useMemo(() => {
    const buyText = t("common:buy").toUpperCase();

    let bFS = buyFontSize;
    let pFS = priceFontSize;

    const measure = (text, size) => {
      const s = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: size,
        fontWeight: "bold",
        wordWrap: true,
        wordWrapWidth: maxTextWidth,
      });
      return TextMetrics.measureText(text, s);
    };

    // First pass: scale each for width (widest single line)
    let buyM = measure(buyText, bFS);
    if (buyM.width > maxTextWidth) {
      bFS = Math.round((bFS * (maxTextWidth / buyM.width)) / 2) * 2;
    }
    let priceM = measure(priceText, pFS);
    if (priceM.width > maxTextWidth) {
      pFS = Math.round((pFS * (maxTextWidth / priceM.width)) / 2) * 2;
    }

    // Second pass: check combined height (accounts for wrapped multi-line text)
    buyM = measure(buyText, bFS);
    priceM = measure(priceText, pFS);
    const combinedH = buyM.height + textGap + priceM.height;
    if (combinedH > maxTextHeight) {
      const shrink = maxTextHeight / combinedH;
      bFS = Math.round((bFS * shrink) / 2) * 2;
      pFS = Math.round((pFS * shrink) / 2) * 2;
    }

    return { scaledBuyFontSize: bFS, scaledPriceFontSize: pFS };
  }, [
    buyFontSize,
    priceFontSize,
    priceText,
    maxTextWidth,
    maxTextHeight,
    textGap,
    t,
  ]);

  const buyTextStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: scaledBuyFontSize,
        fontWeight: "bold",
        fill: 0x000000,
        align: "center",
        wordWrap: true,
        wordWrapWidth: maxTextWidth,
        resolution: dpr,
      }),
    [scaledBuyFontSize, maxTextWidth, dpr],
  );

  const priceTextStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: scaledPriceFontSize,
        fontWeight: "bold",
        fill: 0x000000,
        align: "center",
        wordWrap: true,
        wordWrapWidth: maxTextWidth,
        resolution: dpr,
      }),
    [scaledPriceFontSize, maxTextWidth, dpr],
  );

  // ── Layout calculation (all relative to card frame) ────────────────────
  const titleText = t(tier.titleKey).toUpperCase();
  const subtitleText = t(tier.descKey);

  const titleMetrics = useMemo(
    () => TextMetrics.measureText(titleText, titleStyle),
    [titleText, titleStyle],
  );
  const subtitleMetrics = useMemo(
    () => TextMetrics.measureText(subtitleText, subtitleStyle),
    [subtitleText, subtitleStyle],
  );

  // Vertical layout: padding → title → gap → subtitle → image(fill) → button → padding
  const titleH = titleMetrics.height;
  // const subtitleH = subtitleMetrics.height;
  // The tallest description of the four, so every card's image sits in the
  // same band — the same reason the title area is shared.
  const subtitleH = sharedSubtitleAreaHeight ?? subtitleMetrics.height;
  const buttonAreaH = btnH;

  // Reserved title-row height. When one card's title wraps to more lines
  // than the other (both now share the same font size, but text length still
  // differs), reserving each card's own titleH would push its subtitle up
  // higher than the other card's — using the shared (tallest) height for
  // both keeps the subtitles vertically aligned across cards.
  const titleAreaH = sharedTitleAreaHeight ?? titleH;

  // Image gets remaining vertical space
  // On MOBILE the volatility + xBet rows are used height too: imageRegionBottom
  // stops the band above them, so the SIZE must as well, or the image is sized
  // for a taller band than it is centred in and runs into the text
  // (2026-10-06). Desktop and tablet keep the original sum — see cardFit.
  const usedH =
    paddingTop +
    titleAreaH +
    titleSubGap +
    subtitleH +
    titleSubGap +
    imageBottomGap +
    // (fitImageToBand ? infoBlockGap + infoBlockH : 0) +
    (fitImageToBand ? infoReserveH : 0) +
    buttonAreaH +
    paddingBottom;
  const imageAvailH = Math.max(0, cardHeight - usedH);
  const imageAvailW = contentWidth * 0.92;

  // Scale image to fit available space
  const imageLayout = useMemo(() => {
    if (!featureTexture || imageAvailH <= 0) return null;
    const ar = featureTexture.width / featureTexture.height;
    let w, h;
    if (imageAvailH * ar <= imageAvailW) {
      h = imageAvailH;
      w = h * ar;
    } else {
      w = imageAvailW;
      h = w / ar;
    }
    const scale = tier.imageScale || 1;
    return { width: w * scale, height: h * scale };
  }, [featureTexture, imageAvailH, imageAvailW, tier]);

  // Y positions (all relative to card top-left origin)
  const cx = cardWidth / 2;
  // Title is vertically centered within the shared reserved area — so a
  // single-line title sits centered against a two-line title on the other
  // card, rather than pinned to the top of a taller-than-needed block.
  const titleY = paddingTop + titleAreaH / 2;
  const subtitleY = paddingTop + titleAreaH + titleSubGap + subtitleH / 2;
  // imageY = center of the available space region (between subtitle bottom and button top)
  // const imageRegionTop =
  //   paddingTop + titleAreaH + titleSubGap + subtitleH + titleSubGap;
  // ── The image band now STOPS above the volatility block ──
  //
  // The new design puts two rows between the picture and the BUY button, so the
  // space the image is centred in has to give them up — otherwise the image
  // centres over them and the rows sit on top of it. This is the "lift the
  // images slightly upwards" half of that change, and it is subtractive rather
  // than a nudge, so it stays correct on every card size.
  // const imageRegionBottom =
  //   cardHeight -
  //   paddingBottom -
  //   buttonAreaH -
  //   // infoBlockGap -
  //   // infoBlockH -
  //   infoReserveH -
  //   imageBottomGap;
  // const imageY =
  //   (imageRegionTop + imageRegionBottom) / 2 +
  //   cardHeight * (0.015 + (tier.imageYOffset || 0));
  const buttonY = cardHeight - paddingBottom - buttonAreaH / 2;
  // ── Centred between the description and the BUY button (2026-10-14: "its
  // not center between button and text") ── the band above was lopsided (a
  // bigger gap on top than below) and nudged down 1.5% on top of that. Now
  // the midpoint of the description's own bottom edge and the button's top
  // edge (the art is centred in its texture), the rows' space too when on.
  const descriptionBottom = subtitleY + subtitleMetrics.height / 2;
  // const imageY =
  //   (descriptionBottom + (cardHeight - paddingBottom - buttonAreaH - infoReserveH)) / 2 +
  //   cardHeight * (tier.imageYOffset || 0);
  // A card with `imageTopAtScale` keeps its TOP where the image at that scale
  // would sit centred, and grows downward from there — under the BUY button,
  // which draws over it (the Hold & Win cards, 2026-10-14).
  // const growDown =
  //   tier.imageTopAtScale && imageLayout
  //     ? (imageLayout.height * (1 - tier.imageTopAtScale / (tier.imageScale || 1))) / 2
  //     : 0;
  // const imageY =
  //   (descriptionBottom + (cardHeight - paddingBottom - buttonAreaH - infoReserveH)) / 2 +
  //   growDown +
  //   cardHeight * (tier.imageYOffset || 0);
  const centreY =
    (descriptionBottom + (cardHeight - paddingBottom - buttonAreaH - infoReserveH)) / 2;
  // `alignTopWith`: this card's ART top on the other card's art top — that
  // card's image sized and centred in the same space (the cards share their
  // bands), its visible top found from its transparent margin.
  const alignRef = tier.alignTopWith
    ? BONUS_TIER_CONFIGS.find((c) => c.key === tier.alignTopWith)
    : null;
  const refSize = alignRef
    ? fitImageSize(
        PIXI.Assets.get(alignRef.featureImage),
        imageAvailW,
        imageAvailH,
        alignRef.imageScale || 1,
      )
    : null;
  const imageY =
    (refSize && imageLayout
      ? centreY -
        refSize.height / 2 +
        refSize.height * (alignRef.visibleTop || 0) +
        imageLayout.height / 2 -
        imageLayout.height * (tier.visibleTop || 0)
      : centreY) +
    cardHeight * (tier.imageYOffset || 0);
  // Bottom row first, then the volatility row above it — measured up from the
  // button so the block always sits against it however tall the card is.
  const xBetY =
    cardHeight - paddingBottom - buttonAreaH - infoBlockGap - infoFontSize / 2;
  const volatilityY = xBetY - infoFontSize / 2 - infoRowGap - infoFontSize / 2;

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleBuyClick = useCallback(() => {
    if (!canAfford || isProcessingRef.current) return;
    isProcessingRef.current = true;

    soundManager.play(SOUND_NAME.button_click);
    if (buyButtonContainerRef.current)
      triggerScalePress(buyButtonContainerRef.current);

    onBuy(tier);

    setTimeout(() => {
      isProcessingRef.current = false;
    }, 500);
  }, [canAfford, onBuy, tier]);

  return (
    <Container name={`buy_card_${tier.key}`}>
      {/* Soft drop shadow under the card (ButtonShadow, utils/buttonShadow) */}
      {cardBgTexture && (
        <ButtonShadow
          target={cardBgRef}
          texture={cardBgTexture}
          width={cardWidth}
          height={cardHeight}
        />
      )}
      {/* Card background — popup_frame_bonus texture */}
      {cardBgTexture && (
        <Sprite
          ref={cardBgRef}
          texture={cardBgTexture}
          width={cardWidth}
          height={cardHeight}
          anchor={0}
        />
      )}

      {/* Title */}
      <Text
        text={titleText}
        style={titleStyle}
        anchor={0.5}
        roundPixels={true}
        x={cx}
        y={titleY}
      />

      {/* Subtitle */}
      <Text
        text={subtitleText}
        style={subtitleStyle}
        anchor={0.5}
        roundPixels={true}
        x={cx}
        y={subtitleY}
      />

      {/* Feature image */}
      {imageLayout && featureTexture && (
        <Sprite
          texture={featureTexture}
          anchor={0.5}
          roundPixels={true}
          x={cx}
          y={imageY}
          width={imageLayout.width}
          height={imageLayout.height}
        />
      )}

      {/* ── Volatility ──────────────────────────────────────────────────────

          The word and its dots on ONE line, centred together: the label is
          drawn from the middle of the group leftwards and the dots run
          rightwards from it, so the pair stays centred whatever the label
          translates to. Measured rather than positioned by hand for that
          reason — "Volatility" is not the same width in every language.

          Dots are drawn, not textured. The pagination dots this borrows from
          are CSS circles in PaytableSlider, so there is no asset to reuse —
          only the colours, which are taken from it: #ffc102 filled, white
          empty, matching the slider's active/inactive states. */}
      {SHOW_INFO_ROWS && (
      <VolatilityRow
        cx={cx}
        y={volatilityY}
        filled={tier.volatility || 0}
        label={t("bonus_buy_volatility")}
        style={infoStyle}
        dotSize={dotSize}
        dotGap={dotGap}
        labelGap={dotLabelGap}
      />
      )}

      {/* ── "N x bet" ──
          The multiplier is REAL — feature_options.feature_multipliers, one per
          level, parsed at init. Only the volatility above it is authored. A
          level the API did not advertise renders nothing rather than a wrong
          or zero number, the same rule the price already follows. */}
      {/* {hasPrice && ( */}
      {SHOW_INFO_ROWS && hasPrice && (
        <Text
          // ── Built in JS, NOT through i18n interpolation ──
          //
          // Two attempts at t() dropped the number: positionally
          // (`t(key, "{{n}} x bet", { n })`) it does not interpolate the
          // fallback, and with `defaultValue` in the options it still rendered
          // without it here. The multiplier is the whole point of the line, so
          // it is concatenated and only the WORDS are translated.
          text={`${tier.multiplier} ${t("bonus_buy_x_bet")}`}
          style={infoStyle}
          x={cx}
          y={xBetY}
          anchor={0.5}
        />
      )}

      {/* BUY button */}
      {buttonTexture && (
        <Container
          ref={buyButtonContainerRef}
          name={`buy_button_${tier.key}`}
          x={cx}
          y={buttonY}
        >
          {/* Soft drop shadow under it (ButtonShadow, utils/buttonShadow) */}
          <ButtonShadow
            target={buttonRef}
            texture={canAfford ? buttonTexture : (buttonDisabledTexture ?? buttonTexture)}
            width={btnW}
            height={btnH}
          />
          <Sprite
            ref={buttonRef}
            texture={
              canAfford
                ? buttonTexture
                : (buttonDisabledTexture ?? buttonTexture)
            }
            anchor={0.5}
            width={btnW}
            height={btnH}
            eventMode={canAfford ? "static" : "none"}
            cursor={canAfford ? "pointer" : "default"}
            onpointertap={handleBuyClick}
            onpointerover={canAfford ? handlePointerOver : null}
            onpointerout={canAfford ? handlePointerOut : null}
          />
          {/* BUY text */}
          <Text
            text={t("common:buy").toUpperCase()}
            style={buyTextStyle}
            anchor={{ x: 0.5, y: 1 }}
            roundPixels={true}
            x={0}
            y={-btnH * 0.001}
            eventMode="none"
          />
          {/* Price text */}
          <Text
            text={priceText}
            style={priceTextStyle}
            anchor={{ x: 0.5, y: 0 }}
            roundPixels={true}
            x={0}
            y={btnH * 0.001}
            eventMode="none"
            alpha={0.6}
          />
        </Container>
      )}
    </Container>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// BuyBonusPopup — full-screen popup with two bonus tier cards
// ═════════════════════════════════════════════════════════════════════════════
const BuyBonusPopup = () => {
  const dispatch = useDispatch();
  const { handleSpin } = useReelControl();

  const isOpen = useSelector(selectBonusBuyActive);
  const currentBet = useSelector((state) => state.game.bet);
  const currency = useSelector(selectCurrency);
  const balance = useSelector(selectBalance);
  const bonusBuyMultipliers = useSelector(selectBonusBuyMultipliers);
  // The feature name the API advertised its prices under. Sending Zeus's
  // hardcoded "bonus_buy" to Mad Labs is rejected with 203 invalid_options.
  const bonusBuyFeatureName = useSelector(selectBonusBuyFeatureName);
  // Every feature's prices — each card reads its own feature's level.
  const bonusBuyFeatures = useSelector(selectBonusBuyFeatures);

  // Price each tier from its own level. Levels the API did not advertise stay
  // undefined and the card renders "—" rather than a wrong or zero price.
  const BONUS_TIERS = useMemo(
    () =>
      BONUS_TIER_CONFIGS.map((config) => ({
        ...config,
        // multiplier: bonusBuyMultipliers?.[config.featureLevel],
        multiplier:
          (config.featureName &&
            bonusBuyFeatures?.[config.featureName]?.[config.featureLevel]) ??
          bonusBuyMultipliers?.[config.featureLevel],
      })),
    [bonusBuyMultipliers, bonusBuyFeatures],
  );

  // Spin guards
  const isSpinning = useSelector(selectIsSpinning);
  const counterAnimationFinished = useSelector(selectCounterAnimationFinished);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const spinComplete = useSelector((state) => state.spin.spinComplete);
  const currentRound = useSelector((state) => state.game.currentRound);

  // Animation state
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  // Follows the size only while open or closing; closed, a resize does not
  // re-render it (useDimensionsWhen).
  const {
    dimensions,
    device,
    orientation,
    symbolScale,
    isMobileSmall,
    getResponsiveConfig,
    // uiSymbolSize, (unused since the close-button fix, 2026-10-08)
  } = useDimensionsWhen(isOpen || isClosing);
  const popupContentRef = useRef(null);
  const buyConfirmedRef = useRef(false);
  const selectedTierRef = useRef(null);

  // Scroll state (mobile portrait only)
  const [scrollY, setScrollY] = useState(0);
  const isDragging = useRef(false);
  const lastTouchY = useRef(0);
  const scrollContentRef = useRef(null);
  const viewportMaskRef = useRef(null);

  // Close button hover/press effects
  const {
    buttonRef: closeButtonRef,
    handlePointerOver: closePointerOver,
    handlePointerOut: closePointerOut,
    triggerPress: closeTriggerPress,
    cleanupFilters: closeCleanupFilters,
  } = useButtonBrightness();

  useEffect(() => () => closeCleanupFilters(), [closeCleanupFilters]);

  // ── Responsive scale (symbolScale × deviceMultiplier) ──────────────────
  // const popupScale = useMemo(() => {
  const basePopupScale = useMemo(() => {
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    const cfg =
      POPUP_CONFIG.DEVICE_CONFIGS[realDevice]?.[orientation] ||
      POPUP_CONFIG.DEVICE_CONFIGS[realDevice] ||
      POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP];
    const dm = cfg?.deviceMultiplier ?? 1.0;
    return symbolScale * dm;
  }, [device, orientation, symbolScale, isMobileSmall]);

  // ── The whole row fitted to the screen's HEIGHT (2026-10-30, QA: the BET
  // label cropped at the top on a phone with Safari's bars, 715x414) ── the
  // stack is the BET label + selector, its gap, and the cards. Where it is
  // taller than the screen (less a margin each side), everything shrinks by
  // the same ratio — selector, gaps and cards keep their proportions. A
  // screen it already fits is left exactly as it was. The column layout
  // (phone portrait) scrolls instead, untouched.
  const popupScale = useMemo(() => {
    const columnLayout =
      orientation === PixiSettings.ORIENTATION.PORTRAIT &&
      (device === PixiSettings.DEVICE.MOBILE || isMobileSmall);
    if (columnLayout || !dimensions.height) return basePopupScale;
    const realDevice = isMobileSmall ? PixiSettings.DEVICE.MOBILE_SMALL : device;
    const cfg =
      realDevice === PixiSettings.DEVICE.DESKTOP
        ? POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP] || {}
        : POPUP_CONFIG.DEVICE_CONFIGS[realDevice]?.[orientation] || {};
    const selectorRatio = cfg.betSelectorScaleRatio ?? POPUP_CONFIG.betSelectorScaleRatio;
    const count = BONUS_TIER_CONFIGS.length;
    // The stack's height at popup scale `ps` — the same sums the layout below makes.
    const stackHeight = (ps) => {
      const w = 461 * ps;
      const row = count * w + (count - 1) * w * POPUP_CONFIG.cardGapRatio;
      const rowFit = Math.min(1, (dimensions.width * POPUP_CONFIG.rowMaxWidthRatio) / row);
      return (
        611 * ps * rowFit +
        w * rowFit * POPUP_CONFIG.betSelectorGapRatio +
        BET_SELECTOR_ABOVE_REF * selectorRatio * ps
      );
    };
    const available = dimensions.height * (1 - 2 * POPUP_FIT_MARGIN_RATIO);
    let ps = basePopupScale;
    // A few passes: the row's own width fit makes the height non-linear in ps.
    for (let i = 0; i < 4; i++) {
      const h = stackHeight(ps);
      if (h <= available) break;
      ps *= available / h;
    }
    return ps;
  }, [basePopupScale, device, orientation, isMobileSmall, dimensions.width, dimensions.height]);

  // ── Layout mode (column only for mobile portrait, row for everything else) ──
  const isColumnLayout =
    orientation === PixiSettings.ORIENTATION.PORTRAIT &&
    (device === PixiSettings.DEVICE.MOBILE || isMobileSmall);

  // ── Card dimensions (pinned to original 461×611 reference — texture scales to fit) ────
  //
  // Four cards in a row are far wider than the two this popup was built for,
  // so the row is measured against the screen and the whole set shrinks to
  // fit. Without this the outer cards run off both edges. The column layout
  // is unaffected — it grows downward and scrolls instead.
  const cardCount = BONUS_TIER_CONFIGS.length;
  const baseCardWidth = 461 * popupScale;
  const rowFitScale = useMemo(() => {
    if (isColumnLayout) return 1;
    const gap = baseCardWidth * POPUP_CONFIG.cardGapRatio;
    const rowWidth = cardCount * baseCardWidth + (cardCount - 1) * gap;
    const available = dimensions.width * POPUP_CONFIG.rowMaxWidthRatio;
    return rowWidth > available ? available / rowWidth : 1;
  }, [isColumnLayout, baseCardWidth, cardCount, dimensions.width]);

  const cardWidth = baseCardWidth * rowFitScale;
  const cardHeight = 611 * popupScale * rowFitScale;

  // ── Shared title/description font sizes — both cards use the smaller of
  // the two "would-fit" sizes, so a long title or description on one card
  // doesn't make it look mismatched against a short (unshrunk) one on the
  // other. ────────────────────────────────────────────────────────────────
  const { t } = useTranslation();
  const cardContentWidth = useMemo(() => {
    const padding = cardWidth * POPUP_CONFIG.paddingBottomRatio;
    return cardWidth - padding * 2;
  }, [cardWidth]);

  // const titleFontSize = useMemo(() => {
  // Phone landscape — the bigger text ratios (POPUP_CONFIG, 2026-10-08). The
  // same test as fitCardForImage below.
  const isPhoneLandscape =
    (device === PixiSettings.DEVICE.MOBILE || isMobileSmall) &&
    orientation === PixiSettings.ORIENTATION.LANDSCAPE;
  const titleFitSize = useMemo(() => {
    // const idealTitleFontSize =
    //   Math.round((cardWidth * POPUP_CONFIG.titleFontRatio) / 2) * 2;
    const titleRatio = isPhoneLandscape
      ? POPUP_CONFIG.titleFontRatioMobileLandscape
      : POPUP_CONFIG.titleFontRatio;
    const idealTitleFontSize = Math.round((cardWidth * titleRatio) / 2) * 2;
    return Math.min(
      ...BONUS_TIER_CONFIGS.map((config) =>
        fitFontSizeToWidth(
          t(config.titleKey).toUpperCase(),
          idealTitleFontSize,
          cardContentWidth,
          "bold",
        ),
      ),
    );
  }, [cardWidth, cardContentWidth, t, isPhoneLandscape]);

  // const subtitleFontSize = useMemo(() => {
  const subtitleFitSize = useMemo(() => {
    // const idealSubtitleFontSize =
    //   Math.round((cardWidth * POPUP_CONFIG.subtitleFontRatio) / 2) * 2;
    const subtitleRatio = isPhoneLandscape
      ? POPUP_CONFIG.subtitleFontRatioMobileLandscape
      : POPUP_CONFIG.subtitleFontRatio;
    const idealSubtitleFontSize = Math.round((cardWidth * subtitleRatio) / 2) * 2;
    return Math.min(
      ...BONUS_TIER_CONFIGS.map((config) =>
        fitFontSizeToLines(
          t(config.descKey),
          idealSubtitleFontSize,
          cardContentWidth,
          isPhoneLandscape ? DESC_MAX_LINES_MOBILE_LANDSCAPE : DESC_MAX_LINES,
        ),
      ),
    );
  }, [cardWidth, cardContentWidth, t, isPhoneLandscape]);

  // // ── Shared title-row height — both cards reserve the taller of the two
  // // titles' wrapped heights (at the shared font size above), so if one
  // // title wraps to more lines than the other, the descriptions below still
  // // line up at the same Y instead of the shorter title's card sitting higher. ──
  // const titleAreaHeight = useMemo(() => {
    // const heights = BONUS_TIER_CONFIGS.map((config) => {
      // const style = new TextStyle({
  // ── The IMAGE keeps its full size; the text above it gives way ──
  //
  // The image used to take whatever height was left, and that sum forgot the
  // volatility + xBet rows — so it was sized for a taller band than the one it
  // is centred in and ran into the description and the volatility row ("glued",
  // 2026-10-06). Worse on a cramped card: a wrapped title and a three-line
  // description left the image small. Vladimir: "if needed make text shrink to
  // fit."
  //
  // So the image's full, width-fitted size is the target, and everything ABOVE
  // it — title, description and the three gaps around them — scales down
  // together in CARD_FIT_STEP steps until the image fits its honest band. Never
  // below CARD_FIT_MIN_SCALE; a card with room keeps scale 1, exactly as before.
  // Shared by all four cards, like the font sizes, so the row stays aligned.
  //
  // Heights are measured with the SAME styles BuyCard draws with (wrap width,
  // weight, the description's 1.2 line height), or the budget is a guess.
  //
  // ── MOBILE LANDSCAPE ONLY (standard and small) ──
  //
  // Desktop, tablet and mobile PORTRAIT keep exactly what they had: the fit
  // sizes, scale 1, no shared description height and the original image band.
  // Vladimir, 2026-10-06: "mobile small mobile large landscape only".
  const fitCardForImage =
    (device === PixiSettings.DEVICE.MOBILE || isMobileSmall) &&
    orientation === PixiSettings.ORIENTATION.LANDSCAPE;
  const cardFit = useMemo(() => {
    const cw = cardWidth;
    const titleStyleAt = (size) =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        // fontSize: titleFontSize,
        fontSize: size,
        fontWeight: "bold",
        wordWrap: true,
        wordWrapWidth: cardContentWidth,
      });
      // return TextMetrics.measureText(
        // t(config.titleKey).toUpperCase(),
        // style,
      // ).height;
    // });
    // return Math.max(...heights);
  // }, [titleFontSize, cardContentWidth, t]);
    const subtitleStyleAt = (size) =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: size,
        fontWeight: "500",
        wordWrap: true,
        wordWrapWidth: cardContentWidth,
        lineHeight: Math.round(size * 1.2),
      });
    const tallest = (key, styleAt, size, upper) =>
      Math.max(
        ...BONUS_TIER_CONFIGS.map((config) => {
          const text = t(config[key]);
          return TextMetrics.measureText(
            upper ? text.toUpperCase() : text,
            styleAt(size),
          ).height;
        }),
      );

    // Everything that does NOT scale: paddings, the info rows, the button.
    const btnTexture = getNewUITexture("button_bonus_card");
    const btnAR = btnTexture
      ? btnTexture.width / btnTexture.height
      : 270 / 90;
    const btnH = (cw * POPUP_CONFIG.buyButtonWidthRatio) / btnAR;
    const infoFont = Math.round((cw * POPUP_CONFIG.infoFontRatio) / 2) * 2;
    const infoBlock =
      infoFont * 2 +
      cw * POPUP_CONFIG.infoRowGapRatio +
      cw * POPUP_CONFIG.infoBlockGapRatio;
    const fixedH =
      cw * (POPUP_CONFIG.paddingTopRatio + POPUP_CONFIG.paddingBottomRatio) +
      infoBlock +
      btnH;
    const gapsAtFull =
      cw * (POPUP_CONFIG.titleSubGapRatio * 2 + POPUP_CONFIG.imageBottomGapRatio);

    // The tallest image at full width — what BuyCard draws when it has room.
    const imageW = cardContentWidth * 0.92;
    const imageH = Math.max(
      0,
      ...BONUS_TIER_CONFIGS.map((config) => {
        const texture = PIXI.Assets.get(config.featureImage);
        // return texture?.width > 0 ? (imageW * texture.height) / texture.width : 0;
        // Cleopatra's Fire: each card's own imageScale (BuyCard applies it on
        // top of the fit) — the H&W card's 1.2 needs its room too.
        // return texture?.width > 0
        //   ? ((imageW * texture.height) / texture.width) * (config.imageScale || 1)
        //   : 0;
        // The text fit sizes for the image as it was (textFitImageScale,
        // 2026-10-14): the bigger images never shrink the text.
        return texture?.width > 0
          ? ((imageW * texture.height) / texture.width) *
              (config.textFitImageScale ?? config.imageScale ?? 1)
          : 0;
      }),
    );

    if (!fitCardForImage) {
      return {
        scale: 1,
        titleFontSize: titleFitSize,
        subtitleFontSize: subtitleFitSize,
        titleAreaHeight: tallest("titleKey", titleStyleAt, titleFitSize, true),
        subtitleAreaHeight: undefined,
      };
    }

    const steps = Math.round((1 - CARD_FIT_MIN_SCALE) / CARD_FIT_STEP);
    let fit = null;
    for (let k = 0; k <= steps; k++) {
      const scale = 1 - k * CARD_FIT_STEP;
      const titleSize =
        k === 0 ? titleFitSize : Math.max(8, Math.floor((titleFitSize * scale) / 2) * 2);
      const subtitleSize =
        k === 0
          ? subtitleFitSize
          : Math.max(8, Math.floor((subtitleFitSize * scale) / 2) * 2);
      const titleH = tallest("titleKey", titleStyleAt, titleSize, true);
      const subtitleH = tallest("descKey", subtitleStyleAt, subtitleSize, false);
      fit = {
        scale,
        titleFontSize: titleSize,
        subtitleFontSize: subtitleSize,
        titleAreaHeight: titleH,
        subtitleAreaHeight: subtitleH,
      };
      const band = cardHeight - fixedH - gapsAtFull * scale - titleH - subtitleH;
      if (band >= imageH) break;
    }
    return fit;
  }, [
    cardWidth,
    cardHeight,
    cardContentWidth,
    titleFitSize,
    subtitleFitSize,
    fitCardForImage,
    t,
  ]);

  // ── Center coordinates ────────────────────────────────────────────────
  const deviceConfig = useMemo(() => {
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    const configs = POPUP_CONFIG.DEVICE_CONFIGS;
    if (realDevice === PixiSettings.DEVICE.DESKTOP) {
      return configs[PixiSettings.DEVICE.DESKTOP] || {};
    }
    return configs[realDevice]?.[orientation] || {};
  }, [device, orientation, isMobileSmall]);

  // ── Bet selector scale (tweakable independently from cards) ────────────
  const betSelectorScale =
    popupScale *
    (deviceConfig.betSelectorScaleRatio ?? POPUP_CONFIG.betSelectorScaleRatio);

  const centerX = dimensions.width / 2;
  // const centerY =
  //   dimensions.height / 2 + (deviceConfig.yOffset || 0) * cardHeight;
  // ...and moved down just enough that the BET label clears the top, when it
  // would not (the row layout; the bottom's free room is the limit). A row
  // that already clears it stays where it was (2026-10-30).
  const centerY = useMemo(() => {
    const base = dimensions.height / 2 + (deviceConfig.yOffset || 0) * cardHeight;
    if (isColumnLayout) return base;
    const margin = dimensions.height * POPUP_FIT_MARGIN_RATIO;
    const above =
      cardHeight / 2 +
      cardWidth * POPUP_CONFIG.betSelectorGapRatio +
      BET_SELECTOR_ABOVE_REF * betSelectorScale;
    const top = base - above;
    if (top >= margin) return base;
    const bottomRoom = dimensions.height - margin - (base + cardHeight / 2);
    return base + Math.min(margin - top, Math.max(0, bottomRoom));
  }, [dimensions.height, deviceConfig.yOffset, cardHeight, cardWidth, betSelectorScale, isColumnLayout]);

  const cardGap =
    cardWidth *
    (isColumnLayout
      ? POPUP_CONFIG.cardGapPortraitRatio
      : POPUP_CONFIG.cardGapRatio);

  // ── Bet selector position (above cards) ───────────────────────────────
  const betSelectorGap =
    cardWidth *
    (isColumnLayout
      ? POPUP_CONFIG.betSelectorGapPortraitRatio
      : POPUP_CONFIG.betSelectorGapRatio);
  const betSelectorY = useMemo(() => {
    if (isColumnLayout) {
      return centerY - cardHeight - cardGap / 2 - betSelectorGap;
    }
    return centerY - cardHeight / 2 - betSelectorGap;
  }, [centerY, cardHeight, cardGap, betSelectorGap, isColumnLayout]);

  // ── Close button size (same as ButtonSound / generic UI buttons) ───────
  const closeButtonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );
  const closeButtonSizeMultiplier = useMemo(() => {
    if (device === PixiSettings.DEVICE.DESKTOP) return 0.8;
    if (
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.PORTRAIT &&
      !isMobileSmall
    )
      return 0.45;
    return 0.5;
  }, [device, orientation, isMobileSmall]);
  const closeButtonSize = closeButtonConfig.width;

  // ── Close button position (mirrors ButtonSound padding per device) ──────
  const MAX_TOOLBAR_WIDTH = 1920;
  const closeButtonPos = useMemo(() => {
    const buttonVisualSize = closeButtonSize * closeButtonSizeMultiplier;

    // Y aligned with BET title (betSelectorY + label offset above the bet background)
    const betLabelY = betSelectorY - (popupScale * 110) / 2 - popupScale * 15;

    if (device === PixiSettings.DEVICE.MOBILE || isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        const closeYOffset = isMobileSmall
          ? -cardHeight * 0.05
          : -cardHeight * 0.07;
        const closeXMultiplier = isMobileSmall ? 1.4 : 1.2;
        return {
          x: dimensions.width - buttonVisualSize * closeXMultiplier,
          y: betLabelY + buttonVisualSize * 0.5 + closeYOffset,
        };
      }
      // // Mobile landscape — position to the right of cards, halfway to toolbar edge
      // const card2RightEdge = centerX + cardGap / 2 + cardWidth;
      // const toolbarWidth = Math.min(dimensions.width, uiSymbolSize * 10);
      // const toolbarEdge = (dimensions.width + toolbarWidth) / 2;
      // return {
      //   x: (card2RightEdge + toolbarEdge) / 2,
      //   y: closeButtonSize * 0.8,
      // };
      // Mobile landscape — the X's right edge on the row's right edge (3 Orbs
      // of Zeus e87f8036, 2026-10-08). The old maths took the second card's
      // right edge of the TWO-card popup it was built for; the row's real
      // width holds for any card count.
      const count = BONUS_TIER_CONFIGS.length;
      const rowRightEdge =
        centerX + (count * cardWidth + (count - 1) * cardGap) / 2;
      return {
        x: rowRightEdge - buttonVisualSize / 2,
        y: closeButtonSize * 0.8,
      };
    }

    // Tablet & Desktop — toolbar capped at 1920px
    const toolbarWidth = Math.min(dimensions.width, MAX_TOOLBAR_WIDTH);
    const toolbarOffset = (dimensions.width - toolbarWidth) / 2;

    if (device === PixiSettings.DEVICE.TABLET) {
      return {
        x: toolbarOffset + toolbarWidth - closeButtonSize * 0.8,
        y: closeButtonSize * 0.8,
      };
    }
    // Desktop
    return {
      x: toolbarOffset + toolbarWidth - closeButtonSize,
      y: closeButtonSize * 1.4,
    };
  }, [
    dimensions.width,
    device,
    orientation,
    isMobileSmall,
    closeButtonSize,
    closeButtonSizeMultiplier,
    // uiSymbolSize, (unused since the close-button fix, 2026-10-08)
    betSelectorY,
    popupScale,
    centerX,
    cardGap,
    cardWidth,
    cardHeight,
  ]);

  // ── Card positions — one entry per tier ───────────────────────────────
  // Row in landscape, column in portrait, both centred on (centerX, centerY).
  // Built as a list so the card count is data: adding a fifth tier needs no
  // layout change here.
  const cardPositions = useMemo(() => {
    const count = BONUS_TIERS.length;
    if (isColumnLayout) {
      // ── Hung UNDER the bet selector, not centred as a block ──
      //
      // Centring is what breaks on a card count this popup was not built for.
      // At two cards the block happened to start just below the selector; at
      // four it starts 229px ABOVE the top of the screen, and since `scrollY`
      // clamps to [0, maxScrollY] there is no way to scroll up and reach it.
      //
      // The selector's own position is untouched — this is the same first-card
      // top the centred form produced at two cards, arrived at from the anchor
      // above rather than from the middle:
      //
      //   centred: centerY - cardHeight - cardGap/2
      //   here:    betSelectorY + betSelectorGap        (identical, by
      //            betSelectorY's own definition)
      //
      // So two cards land pixel-for-pixel where they always did, and any
      // further card simply extends the column downward into the scroll.
      const top = betSelectorY + betSelectorGap;
      return BONUS_TIERS.map((_, i) => ({
        x: centerX - cardWidth / 2,
        y: top + i * (cardHeight + cardGap),
      }));
    }
    const totalW = count * cardWidth + (count - 1) * cardGap;
    const left = centerX - totalW / 2;
    return BONUS_TIERS.map((_, i) => ({
      x: left + i * (cardWidth + cardGap),
      y: centerY - cardHeight / 2,
    }));
  }, [
    BONUS_TIERS,
    centerX,
    centerY,
    cardWidth,
    cardHeight,
    cardGap,
    isColumnLayout,
    betSelectorY,
    betSelectorGap,
  ]);

  // An unpriced tier is always affordable from the client's point of view —
  // the server prices it and rejects if the balance is short.
  const canAffordTier = useCallback(
    (tier) => {
      const hasPrice =
        typeof tier.multiplier === "number" && tier.multiplier > 0;
      if (!hasPrice) return true;
      return balance >= currentBet * tier.multiplier;
    },
    [balance, currentBet],
  );

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (isClosing) return;
    soundManager.play(SOUND_NAME.button_click);
    if (closeButtonRef.current) closeTriggerPress();
    setIsClosing(true);
  }, [isClosing, closeButtonRef, closeTriggerPress]);

  const handleBuy = useCallback(
    (tier) => {
      if (!canAffordTier(tier)) return;
      const hasPrice =
        typeof tier.multiplier === "number" && tier.multiplier > 0;

      // Spin guards
      const hasWin = currentRound?.win && currentRound.win > 0;
      const winNotFullyDisplayed =
        hasWin && spinComplete && !counterAnimationFinished;
      if (isSpinning || winNotFullyDisplayed || criticalAnimationPlaying)
        return;

      // Mark confirmed and store selected tier for spin trigger on close
      buyConfirmedRef.current = true;
      selectedTierRef.current = { ...tier, hasPrice };
      setIsClosing(true);
    },
    [
      canAffordTier,
      currentRound,
      spinComplete,
      counterAnimationFinished,
      isSpinning,
      criticalAnimationPlaying,
    ],
  );

  // ── Scroll (mobile portrait only) ────────────────────────────────────────
  // Max scroll = how far the LAST card's bottom overshoots the screen.
  const lastCardPos = cardPositions[cardPositions.length - 1];
  const maxScrollY =
    isColumnLayout && lastCardPos
      ? Math.max(0, lastCardPos.y + cardHeight - dimensions.height + 40)
      : 0;

  const handleScrollPointerDown = useCallback(
    (e) => {
      if (!isColumnLayout) return;
      if (e.data.pointerType !== "touch" && e.data.pointerType !== "pen")
        return;
      isDragging.current = true;
      lastTouchY.current = e.data.global.y;
    },
    [isColumnLayout],
  );

  const handleScrollPointerMove = useCallback(
    (e) => {
      if (!isDragging.current || !isColumnLayout) return;
      if (e.data.pointerType !== "touch" && e.data.pointerType !== "pen")
        return;
      e.stopPropagation();
      const currentY = e.data.global.y;
      const delta = (lastTouchY.current - currentY) * 2.0;
      lastTouchY.current = currentY;
      setScrollY((prev) => Math.max(0, Math.min(prev + delta, maxScrollY)));
    },
    [isColumnLayout, maxScrollY],
  );

  const handleScrollPointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // Reset scroll when popup closes or layout changes
  useEffect(() => {
    if (!isOpen) setScrollY(0);
  }, [isOpen]);
  useEffect(() => {
    setScrollY(0);
  }, [isColumnLayout]);

  // Apply viewport mask to clip scrollable content when scrolling is needed
  useEffect(() => {
    if (scrollContentRef.current) {
      if (isColumnLayout && maxScrollY > 0 && viewportMaskRef.current) {
        scrollContentRef.current.mask = viewportMaskRef.current;
      } else {
        scrollContentRef.current.mask = null;
      }
    }
  }, [isColumnLayout, maxScrollY]);

  // Wheel scroll handler (desktop)
  const handleScrollWheel = useCallback(
    (event) => {
      if (!isColumnLayout) return;
      const scrollSpeed = 30;
      const newScrollY =
        scrollY + (event.deltaY > 0 ? scrollSpeed : -scrollSpeed);
      const clampedScrollY = Math.max(0, Math.min(newScrollY, maxScrollY));
      if (clampedScrollY !== scrollY) {
        event.preventDefault();
        event.stopPropagation();
        setScrollY(clampedScrollY);
      }
    },
    [isColumnLayout, scrollY, maxScrollY],
  );

  // ── Open animation (overlay fade → content fade, no scale) ─────────────
  useEffect(() => {
    if (isOpen && !isClosing) {
      // Fade in overlay
      anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_ALPHA,
        duration: OVERLAY_FADE_MS,
        easing: "easeOutQuad",
        update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
      });

      // Fade in content (slight delay after overlay)
      if (popupContentRef.current) {
        popupContentRef.current.alpha = 0;
        anime({
          targets: popupContentRef.current,
          alpha: 1,
          duration: 200,
          delay: 100,
          easing: "easeOutQuad",
        });
      }
    }
  }, [isOpen, isClosing]);

  // ── Close animation (content fade → overlay fade) ─────────────────────
  useEffect(() => {
    if (!isClosing) return;

    let overlayAnim = null;
    let contentAnim = null;

    // Fade out content
    if (popupContentRef.current) {
      contentAnim = anime({
        targets: popupContentRef.current,
        alpha: 0,
        duration: 200,
        easing: "easeInQuad",
      });
    }

    // Fade out overlay (slight delay after content)
    overlayAnim = anime({
      targets: { alpha: OVERLAY_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_MS,
      delay: 100,
      easing: "easeInQuad",
      update: (anim) => setOverlayAlpha(anim.animations[0].currentValue),
      complete: () => {
        setOverlayAlpha(0);
        dispatch(closeBonusBuy());
        setIsClosing(false);

        // Trigger spin after popup is fully closed
        if (buyConfirmedRef.current) {
          buyConfirmedRef.current = false;
          const tier = selectedTierRef.current;
          selectedTierRef.current = null;
          handleSpin({
            // purchasedFeature: bonusBuyFeatureName || "bonus_buy",
            // The card's own feature — free spins or Hold & Win.
            purchasedFeature: tier?.featureName || bonusBuyFeatureName || "bonus_buy",
            purchasedLevel: tier?.featureLevel || "1",
            // Omitted when the level has no advertised price — sending NaN
            // here would poison the balance bookkeeping downstream.
            ...(tier?.hasPrice
              ? { bonusCost: currentBet * tier.multiplier }
              : {}),
          });
        }
      },
    });

    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (contentAnim) contentAnim.pause();
    };
  }, [isClosing, dispatch, handleSpin, currentBet, bonusBuyFeatureName]);

  // ── Draw overlay ──────────────────────────────────────────────────────
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  // ── Early return ──────────────────────────────────────────────────────
  if (!isOpen && !isClosing) return null;

  return (
    <Container
      name="buy_bonus_popup"
      zIndex={10000}
      sortableChildren
      eventMode="static"
    >
      {/* Dark overlay — catches stray clicks, blocks game underneath */}
      <Graphics
        draw={drawOverlay}
        alpha={overlayAlpha}
        eventMode="static"
        zIndex={0}
      />

      {/* Animated content container */}
      <Container name="popup_content" ref={popupContentRef} zIndex={1}>
        {/* Scroll event catcher — fixed in screen space, hitArea never moves */}
        <Container
          name="scroll_event_catcher"
          eventMode={isColumnLayout ? "static" : "passive"}
          onpointerdown={handleScrollPointerDown}
          onpointermove={handleScrollPointerMove}
          onpointerup={handleScrollPointerUp}
          onpointercancel={handleScrollPointerUp}
          onpointerleave={handleScrollPointerUp}
          onwheel={handleScrollWheel}
          // {...(isColumnLayout
          //   ? {
          //       hitArea: new PIXI.Rectangle(
          //         0,
          //         0,
          //         dimensions.width,
          //         dimensions.height,
          //       ),
          //     }
          //   : {})}
          // ── null, EXPLICITLY, outside the column (2026-10-08) ── @pixi/react
          // never resets a prop that just disappears (applyDefaultProps walks
          // the NEW props only), so the portrait rectangle stayed on this
          // container after a resize back to the row — and a hitArea prunes
          // the hit test for everything inside it: every card and BUY button
          // outside the old portrait-sized rectangle stopped taking clicks.
          hitArea={
            isColumnLayout
              ? new PIXI.Rectangle(0, 0, dimensions.width, dimensions.height)
              : null
          }
        >
          {/* Viewport mask — clips scrollable content to visible area */}
          {isColumnLayout && maxScrollY > 0 && (
            <Graphics
              ref={viewportMaskRef}
              name="viewport_mask"
              renderable={false}
              draw={(g) => {
                g.clear();
                g.beginFill(0x000000, 1);
                g.drawRect(0, 0, dimensions.width, dimensions.height);
                g.endFill();
              }}
            />
          )}

          {/* Scrollable content — only this moves */}
          <Container
            ref={scrollContentRef}
            name="scrollable_content"
            y={isColumnLayout ? -scrollY : 0}
          >
            {/* Bet selector — above cards */}
            <BonusBetSelector
              popupScale={betSelectorScale}
              position={{ x: centerX, y: betSelectorY }}
            />

            {/* One card per buy level */}
            {BONUS_TIERS.map((tier, i) => (
              <Container
                key={tier.key}
                x={cardPositions[i].x}
                y={cardPositions[i].y}
              >
                <BuyCard
                  tier={tier}
                  cardWidth={cardWidth}
                  cardHeight={cardHeight}
                  currentBet={currentBet}
                  currency={currency}
                  canAfford={canAffordTier(tier)}
                  onBuy={handleBuy}
                  // sharedTitleFontSize={titleFontSize}
                  // sharedSubtitleFontSize={subtitleFontSize}
                  // sharedTitleAreaHeight={titleAreaHeight}
                  sharedTitleFontSize={cardFit.titleFontSize}
                  sharedSubtitleFontSize={cardFit.subtitleFontSize}
                  sharedTitleAreaHeight={cardFit.titleAreaHeight}
                  sharedSubtitleAreaHeight={cardFit.subtitleAreaHeight}
                  sharedLayoutScale={cardFit.scale}
                  fitImageToBand={fitCardForImage}
                />
              </Container>
            ))}
          </Container>
        </Container>

        {/* Close button — outside scroll area, always fixed */}
        <Sprite
          ref={closeButtonRef}
          name="popup_close_button"
          texture={getNewUITexture("button_x")}
          anchor={0.5}
          width={closeButtonSize * closeButtonSizeMultiplier}
          height={closeButtonSize * closeButtonSizeMultiplier}
          x={closeButtonPos.x}
          y={closeButtonPos.y}
          eventMode="static"
          cursor="pointer"
          onpointertap={handleClose}
          onpointerover={closePointerOver}
          onpointerout={closePointerOut}
        />
      </Container>
    </Container>
  );
};

export default BuyBonusPopup;
